import { faChevronRight } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  FlexBox,
  JoonUIColor,
  SPACING,
  TherapistIcon,
  Typography,
  UserRole,
} from "@joonapp/web-shared"

import DataTaskCard from "./dataTasks/DataTaskCard"
import useDataTasksModalStore from "./dataTasks/useDataTasksModalStore"
import Card from "../../components/card/Card"
import ArrowCircleRightIcon from "../../components/icons/ArrowCircleRightIcon"
import ParentAvatar from "../../components/parentAvatar/ParentAvatar"
import { completeQuestStatuses, openQuestStatuses } from "../../constants"
import {
  useIncidentTemplatesQuery,
  useParentTaskQuery,
  useAllQuestsQuery,
  useUserQuery,
} from "../../networking/queries"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { useRouter } from "../../util/router"
import CareTeamAvatar from "../care/CareTeamAvatar"
import useCareTeam from "../care/useCareTeam"
import useCoachRoutineQuests from "../coach/routine/useCoachRoutineQuests"
import ReportIncidentButton from "../me/incidents/ReportIncidentButton"
import ParentTaskCard from "../me/parentTasks/ParentTaskCard"
import { useDisplayParentTaskSection } from "../me/parentTasks/useDisplayParentTasksSection"
import useParentTasksModalStore from "../me/parentTasks/useParentTasksModalStore"
import useQuestBoardStore from "../questBoard/useQuestBoardStore"

const AcademyActions = () => {
  const { data: incidentTemplates } = useIncidentTemplatesQuery()
  const { data: parentTasks } = useParentTaskQuery()
  const { displayParentTasksSection } = useDisplayParentTaskSection()
  const router = useRouter()
  const { careTeamUserId } = router.query
  const { hasTherapist, hasCoach } = useCareTeam()
  const { coachRoutineQuests, isLoading: isLoadingCoachRoutine } =
    useCoachRoutineQuests()
  const dataTasks = incidentTemplates?.parent_training_templates.filter(
    // if on a care team user page, only show tasks assigned to that user
    (task) => !careTeamUserId || task.assigner.id === Number(careTeamUserId)
  )
  const quickTherapistTasks = parentTasks?.filter(
    (task) => task.series.assigner_profile?.role === UserRole.THERAPIST
  )

  // Quick todos
  const selectedTherapistProfile =
    quickTherapistTasks?.[0]?.series?.assigner_profile
  const sortedTasks =
    quickTherapistTasks?.sort((task) =>
      completeQuestStatuses.includes(task.status) ? 1 : -1
    ) || []

  const hasRemainingTherapistTasks = !sortedTasks?.every((task) =>
    completeQuestStatuses.includes(task.status)
  )

  const numIncompleteParentQuests =
    parentTasks?.filter(
      (task) =>
        task.series.assigner_profile?.role === UserRole.PARENT &&
        !completeQuestStatuses.includes(task.status)
    ).length || 0

  const numIncompleteCoachRoutineQuests = coachRoutineQuests.filter((quest) =>
    openQuestStatuses.includes(quest.status)
  ).length

  if (!displayParentTasksSection) return <></>

  return (
    <FlexBox direction="column" gap={SPACING.space2}>
      {careTeamUserId ? (
        <>
          {/* TODO: Should we only be showing this ReportIncidentButton if there are child action templates? */}
          {hasTherapist && (
            <div className="tour-step-3" style={{ width: "100%" }}>
              <ReportIncidentButton />
            </div>
          )}
          {dataTasks?.length === 1 ? (
            <DataTaskCard dataTask={dataTasks[0]} />
          ) : (
            dataTasks && dataTasks?.length > 1 && <DataTasksCard />
          )}
          {sortedTasks.length === 1 ? (
            <ParentTaskCard task={sortedTasks[0]} />
          ) : (
            hasRemainingTherapistTasks &&
            selectedTherapistProfile && (
              <ParentTasksCard
                therapistId={selectedTherapistProfile?.user_id}
              />
            )
          )}
        </>
      ) : (
        <>
          {numIncompleteParentQuests > 0 && (
            <ParentQuestsCard
              numIncompleteParentQuests={numIncompleteParentQuests}
            />
          )}
          {hasCoach &&
            !isLoadingCoachRoutine &&
            numIncompleteCoachRoutineQuests > 0 && <CoachRoutineTasksCard />}
          {/* TODO: Should we only be showing this ReportIncidentButton if there are child action templates? */}
          {hasTherapist && <TherapistTasksCard />}
          {hasTherapist && (
            <div style={{ width: "100%" }}>
              <ReportIncidentButton />
            </div>
          )}
        </>
      )}
    </FlexBox>
  )
}

export default AcademyActions

const DataTasksCard = () => {
  const { onOpen: openDataTasksModal } = useDataTasksModalStore()

  return (
    <Card
      title="Practice tasks"
      buttonComponent={<ArrowCircleRightIcon />}
      onClick={() => openDataTasksModal()}
    />
  )
}

const ParentTasksCard = ({ therapistId }: { therapistId: number }) => {
  const { onOpen: openParentTasksModal } = useParentTasksModalStore()

  return (
    <Card
      title="Quick to-dos"
      buttonComponent={<ArrowCircleRightIcon />}
      onClick={() => {
        openParentTasksModal(therapistId)
        trackAnalyticEvent(ANALYTIC_EVENTS.OPEN_PARENT_TASKS_SHEET)
      }}
    />
  )
}

const ParentQuestsCard = ({
  numIncompleteParentQuests,
}: {
  numIncompleteParentQuests: number
}) => {
  const router = useRouter()
  const { user } = useUserQuery()
  const { setSelectedUserId } = useQuestBoardStore()

  if (!user) return null

  return (
    <Card
      title={
        <FlexBox direction="row" align="center" gap={SPACING.space2}>
          <ParentAvatar
            avatar={user.parent_doter.type}
            size={24}
            className="rounded-full"
          />
          <Typography variant="bodySmall">
            {numIncompleteParentQuests} incomplete quests
          </Typography>
        </FlexBox>
      }
      buttonComponent={
        <FontAwesomeIcon
          icon={faChevronRight}
          color={JoonUIColor.icon.neutral}
        />
      }
      onClick={() => {
        setSelectedUserId(user.id)
        router.push("/quests")
        trackAnalyticEvent(ANALYTIC_EVENTS.OPEN_PARENT_TASKS_SHEET)
      }}
      type="secondary"
    />
  )
}

const TherapistTasksCard = () => {
  const router = useRouter()
  const { user } = useUserQuery()
  const { firstTherapist } = useCareTeam()
  const { data: incidentTemplates } = useIncidentTemplatesQuery()
  const dataTasks = incidentTemplates?.parent_training_templates || []
  const { data: quests } = useAllQuestsQuery()
  const numTasksFromTherapist =
    quests?.filter(
      (quest) =>
        quest?.assigner_profile?.role === UserRole.THERAPIST &&
        quest.assigner_profile?.user_id === user?.id
    ).length || 0
  const numTasksFromData = dataTasks.length + numTasksFromTherapist

  if (numTasksFromData === 0) return null

  if (!firstTherapist) return null

  return (
    <Card
      title={
        <FlexBox direction="row" align="center" gap={SPACING.space2}>
          <TherapistIcon color={JoonUIColor.icon.accent} size={24} />
          <Typography variant="bodySmall">
            {numTasksFromData} tasks from your therapist
          </Typography>
        </FlexBox>
      }
      buttonComponent={
        <FontAwesomeIcon
          icon={faChevronRight}
          color={JoonUIColor.icon.neutral}
        />
      }
      onClick={() => {
        router.push(`/care/${firstTherapist.user.id}/academy`)
      }}
      type="secondary"
    />
  )
}

const CoachRoutineTasksCard = () => {
  const router = useRouter()
  const { firstCoach } = useCareTeam()
  const coachName = firstCoach?.nickname
    ? `Coach ${firstCoach?.nickname}`
    : firstCoach?.user.name
      ? `Coach ${firstCoach?.user.name}`
      : "your coach"
  const { coachRoutineQuests } = useCoachRoutineQuests()
  const numIncompleteCoachRoutineQuests = coachRoutineQuests.filter((quest) =>
    openQuestStatuses.includes(quest.status)
  ).length

  return (
    <Card
      title={
        <FlexBox direction="row" align="center" gap={SPACING.space2}>
          {firstCoach && <CareTeamAvatar profile={firstCoach} size="sm" />}
          <Typography variant="bodySmall">
            {numIncompleteCoachRoutineQuests} task
            {numIncompleteCoachRoutineQuests === 1 ? "" : "s"} from {coachName}
          </Typography>
        </FlexBox>
      }
      buttonComponent={
        <FontAwesomeIcon
          icon={faChevronRight}
          color={JoonUIColor.icon.neutral}
        />
      }
      onClick={() => {
        trackAnalyticEvent(ANALYTIC_EVENTS.VIEW_NEW_COACH_ROUTINE)
        router.push(`/coach/${firstCoach?.user.id}/routine`)
      }}
      type="secondary"
    />
  )
}
